import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, PropsWithChildren, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showError } from "@/utils/toast";
import { AuthProvider } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import { CookieConsentProvider } from "@/context/CookieConsentContext";
import AdRevenueModal from "@/components/AdRevenueModal";
import { AdModalProvider, useAdModal } from "@/context/AdModalContext";
import SupportBanner from "@/components/SupportBanner";
import UsernameWarningBanner from "@/components/UsernameWarningBanner";
import BanStatusChecker from "@/components/BanStatusChecker";
import CookieConsent from "@/components/CookieConsent";

import Index from "./pages/Index";
import Index2 from "./pages/Index";
import MaintenancePage from "./pages/MaintenancePage";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import GameShow from "./pages/GameShow";
import EgameShow from "./pages/EgameShow"; // Új komponens importálása
import Translators from "./pages/Translators";
import DownloadShow from "./pages/DownloadShow";
import UserSettings from "./pages/UserSettings";
import Register from "./pages/Register";
import AdminPanel from "./pages/AdminPanel";
import AdminBlog from "./pages/AdminBlog"; // Átnevezve az admin blogra
import Blog from "./pages/Blog"; // A publikus blog
import BlogShow from "./pages/BlogShow"; // Importáljuk az új komponenst
import NewGame from "./pages/NewGame";
import Statistics from "./pages/Statistics";
import Forum from "./pages/Forum";
import ForumTopicList from "./pages/ForumTopicList";
import ForumTopicShow from "./pages/ForumTopicShow";
import Support from "./pages/Support";
import FAQ from "./pages/FAQ";
import Deleted from "./pages/Deleted";
import AdminUsers from "./pages/AdminUsers";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import AdminTags from "./pages/AdminTags";
import AdminTranslators from "./pages/AdminTranslators";
import AdminTranslatorApplications from "./pages/AdminTranslatorApplications";
import AdminLogs from "./pages/AdminLogs";
import AdminNotFoundLogs from "./pages/AdminNotFoundLogs";
import AdminSupportForms from "./pages/AdminSupportForms";
import AdminSiteUpdates from "./pages/AdminSiteUpdates";
import Chat from "./pages/Chat"; // Importáljuk az új Chat komponenst
import EmailConfirmationok from "./pages/EmailConfirmationok"; // Importálva az új olda
import EmailConfirmationnok from "./pages/EmailConfirmationnok"; // Importálva az új olda
import ResetPassword from "./pages/ResetPassword"; // Importálva az új oldal
import TranslatorProfile from "./pages/TranslatorProfile";
import TranslatorSettings from "./pages/TranslatorSettings";
import Videos from "./pages/Videos"; // Import the new Videos page
import ForgotPassword from "./pages/ForgotPassword";
import Autotranslator from "./pages/Autotranslator"; // Import new Autotranslator page
import Requests from "./pages/Requests"; // Import new Requests page
import GameRequests from "./pages/GameRequests";
import GameRequestShow from "./pages/GameRequestShow";
import ReportGameData from "./pages/ReportGameData"; //
import RequestUpdateStep1 from "./pages/RequestUpdateStep1"; // Import new step components
import RequestUpdateStep2 from "./pages/RequestUpdateStep2";
import RequestUpdateStep3 from "./pages/RequestUpdateStep3";
import AdminTranslationStats from "./pages/AdminTranslationStats"; // Import the new stats page
import AdFree from "./pages/AdFree"; // Import the new AdFree page
import TranslatorTranslationStats from "./pages/TranslatorTranslationStats"; // Import the new translator stats page
import AdminYoutubeSuggestions from "./pages/AdminYoutubeSuggestions"; // Import the new youtube suggestions page
import AdminAdFree from "./pages/AdminAdFree"; // Import the new AdminAdFree page
import AdminDisabledAds from "./pages/AdminDisabledAds"; // Import the new disabled ads page
import AdminDisabledCommentsGames from "./pages/AdminDisabledCommentsGames";
import AdminGameComments from "./pages/AdminGameComments"; // Import the new AdminGameComments page
import AdminGames18Plus from "./pages/AdminGames18Plus"; // Import the new 18+ games page
import AdminThemeSettings from "./pages/AdminThemeSettings"; // Import the new Theme Settings page
import AllHowTos from "./pages/AllHowTos"; // Import the new HowTos page
import SubscriptionsPage from "./pages/Subscriptions.tsx"; // Új feliratkozások oldal
import GitSyncSettings from "./pages/GitSyncSettings"; // Új Git Sync beállítások oldal
import AdminForumPosts from "./pages/AdminForumPosts"; // Import the new AdminForumPosts page
import CommunityUpload from "./pages/CommunityUpload"; // Új közösségi feltöltés oldal
import AdminCommunitySubmissions from "./pages/AdminCommunitySubmissions"; // Új közösségi beküldések admin oldal
import SiteTheme from "@/pages/SiteTheme";
import UnsubscribePage from "@/pages/UnsubscribePage"; // Import the new UnsubscribePage
import AdminEgameStats from "./pages/AdminEgameStats"; // Import the new Egame Stats page
import UserThemeSettings from "./pages/UserThemeSettings"; // Import the new UserThemeSettings page
import ModeratorPanel from "./pages/ModeratorPanel";
import ModeratorSteamDB from "./pages/ModeratorSteamDB";
import ImageModeration from "./pages/ImageModeration";
import ModeratorDownloads from "./pages/ModeratorDownloads";
import ModeratorTagsGenres from "./pages/ModeratorTagsGenres";
import ModeratorTopGames from "./pages/ModeratorTopGames";
import ModeratorHardlock from "./pages/ModeratorHardlock";
import AdminEmojis from "./pages/AdminEmojis"; // Import the new AdminEmojis page
import AdminCookies from "./pages/AdminCookies"; // Import the new AdminCookies page
import AdminPolls from "./pages/AdminPolls"; // Import the new AdminPolls page
import AdminDailyGame from "./pages/AdminDailyGame"; // Import the new AdminDailyGame page
import AdminRecommendations from "./pages/AdminRecommendations"; // Import the new AdminRecommendations page
import AdminDuplicateUploads from "./pages/AdminDuplicateUploads"; // Import the new AdminDuplicateUploads page
import AdminBugReports from "./pages/AdminBugReports"; // Import the new AdminBugReports page
import AdminDownstat from "./pages/AdminDownstat"; // Import the new AdminDownstat page
import AdminRules from "./pages/AdminRules"; // Import the new AdminRules page
import AdminCommunityUploadSettings from "./pages/AdminCommunityUploadSettings"; // Import the new AdminCommunityUploadSettings page
import AdminGameUploadSettings from "./pages/AdminGameUploadSettings"; // Import the new AdminGameUploadSettings page
import AdminDownloadSettings from "./pages/AdminDownloadSettings";
import Privacy from "./pages/Privacy"; // Import the new Privacy page
import Xxx from "./pages/Xxx"; // Import the new Xxx page
import PrivacyGuard from "@/components/PrivacyGuard"; // Import the PrivacyGuard
import ApplyTranslator from "./pages/ApplyTranslator"; // Import the new ApplyTranslator page
import Release from "./pages/Release"; // Import the Release page
import Release2 from "./pages/Release2";
import BugReport from "./pages/BugReport"; // Import the new BugReport page
import "bootstrap-icons/font/bootstrap-icons.css";
// import Snowfall from "@/components/Snowfall"; // Snowfall effect removed
// importok...
const queryClient = new QueryClient();

const BannedAlert = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('banned') === 'true') {
      const provider = params.get('provider') || '';
      showError(`🚫 A fiókodat bannolták a(z) ${provider} bejelentkezéskor. Nem tudsz bejelentkezni.`);
      // Töröld a banned paramétert az URL-ből
      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PasswordCheckWrapper = ({ children }: PropsWithChildren) => {
  const { currentUser, isLoggedIn, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Csak akkor fussunk le, ha a betöltés befejeződött
    if (loading) {
      return;
    }

    // ELSŐDLEGES: Az adatvédelmi szabályzat elfogadása szükséges
    // Ez a PrivacyGuard kezeli, így itt nem kell rá vizsgálni

    // MÁSODLAGOS: Ha az adatvédelmi szabályzat elfogadva (és nem OmniAuth vagy már van neve), 
    // akkor ellenőrizzük a jelszót
    if (isLoggedIn && currentUser && currentUser.privacy_accepted && !currentUser.has_password) {
      // Engedélyezzük a hozzáférést a beállításokhoz és a kijelentkezéshez, de máshonnan átirányítunk.
      const allowedPaths = ["/settings", "/logout", "/privacy"];
      if (!allowedPaths.includes(location.pathname)) {
        navigate("/settings", { replace: true });
      }
    }
  }, [isLoggedIn, currentUser, loading, navigate, location.pathname]);

  return <>{children}</>;
};

const MaintenanceGuard = ({ children }: PropsWithChildren) => {
  const { pathname } = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        // Ellenőrizz LocalStorage cache-t először (5 percenként frissítve)
        const cached = localStorage.getItem('maintenanceMode');
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < 5 * 60 * 1000) { // 5 percig érvényes
            setIsMaintenanceMode(data);
            setLoading(false);
            return;
          }
        }

        const response = await fetch('/api/admin_texts/server_message');
        if (response.ok) {
          const data = await response.json();
          const enabled = data.enabled || false;

          // Cache-elj LocalStorage-be
          localStorage.setItem('maintenanceMode', JSON.stringify({
            data: enabled,
            timestamp: Date.now()
          }));

          setIsMaintenanceMode(enabled);
        } else {
          setIsMaintenanceMode(false);
        }
      } catch (error) {
        console.error("Failed to check maintenance mode:", error);
        setIsMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };

    checkMaintenanceMode();
  }, []);

  // Protected útvonalak
  const isProtectedPath =
    pathname === '/' ||
    pathname === '/xxx' ||
    pathname.startsWith('/games/');

  // Ha karbantartás van és protected útvonal, mutassa a maintenance oldalt
  if (!loading && isMaintenanceMode && isProtectedPath) {
    return <MaintenancePage />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { isAdmin, isTranslator, isRealModerator } = useAuth();

  return (
    <Routes>
      <Route path="/" element={<Index2 />} />
      <Route path="/index_old" element={isAdmin ? <Index /> : <NotFound />} />
      <Route path="/dailygames/:date" element={<Index />} />
      <Route path="/login" element={<Login />} />
      <Route path="/release" element={<Release />} />
      <Route path="/release2" element={<Release2 />} />
      {isAdmin && (
        <>
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/ad-free" element={<AdminAdFree />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
          <Route path="/admin/tags" element={<AdminTags />} />
          <Route path="/admin/translators" element={<AdminTranslators />} />
          <Route path="/admin/translator-applications" element={<AdminTranslatorApplications />} />
          <Route path="/admin/logs" element={<AdminLogs />} />
          <Route path="/admin/not-found-logs" element={<AdminNotFoundLogs />} />
          <Route path="/admin/support-forms" element={<AdminSupportForms />} />
          <Route path="/admin/translation-stats" element={<AdminTranslationStats />} />
          <Route path="/admin/blog" element={<AdminBlog />} />

          <Route path="/admin/site-updates" element={<AdminSiteUpdates />} />
          <Route path="/admin/ads-disabled" element={<AdminDisabledAds />} />
          <Route path="/admin/disabled-comments-games" element={<AdminDisabledCommentsGames />} />
          <Route path="/admin/forum-posts" element={<AdminForumPosts />} />
          <Route path="/admin/theme-settings" element={<AdminThemeSettings />} />
          <Route path="/admin/game-comments" element={<AdminGameComments />} />
          <Route path="/admin/games-18plus" element={<AdminGames18Plus />} />
          <Route path="/admin/git-sync-settings" element={<GitSyncSettings />} />
          <Route path="/admin/site-theme" element={<SiteTheme />} />
          <Route path="/admin/egame-stats" element={<AdminEgameStats />} />
          <Route path="/admin/emojis" element={<AdminEmojis />} />
          <Route path="/admin/cookies" element={<AdminCookies />} />
          <Route path="/admin/polls" element={<AdminPolls />} />
          <Route path="/admin/daily-game" element={<AdminDailyGame />} />
          <Route path="/admin/recommendations" element={<AdminRecommendations />} />
          <Route path="/admin/duplicate-uploads" element={<AdminDuplicateUploads />} />
          <Route path="/admin/bug-reports" element={<AdminBugReports />} />
          <Route path="/admin/downstat" element={<AdminDownstat />} />
          <Route path="/admin/rules" element={<AdminRules />} />
          <Route path="/admin/community-upload-settings" element={<AdminCommunityUploadSettings />} />
          <Route path="/admin/game-upload-settings" element={<AdminGameUploadSettings />} />
          <Route path="/admin/downloads" element={<AdminDownloadSettings />} />
        </>
      )}
      {(isAdmin || isRealModerator) && (
        <>
          <Route path="/deleted" element={<Deleted />} />
        </>
      )}
      {isTranslator && (
        <>
          <Route path="/admin/youtube-suggestions" element={<AdminYoutubeSuggestions />} />
          <Route path="/newgame" element={<NewGame />} />
          <Route path="/settings/translator" element={<TranslatorSettings />} />
          <Route path="/translator/statistics" element={<TranslatorTranslationStats />} />
          <Route path="/how-tos" element={<AllHowTos />} />
          <Route path="/moderator" element={<ModeratorPanel />} />
          <Route path="/moderator/steamdb" element={<ModeratorSteamDB />} />
          <Route path="/moderator/steamdb/:gameId" element={<ModeratorSteamDB />} />
          <Route path="/moderator/image-moderation" element={<ImageModeration />} />
          <Route path="/moderator/downloads" element={<ModeratorDownloads />} />
          <Route path="/moderator/tags-genres" element={<ModeratorTagsGenres />} />
          <Route path="/moderator/top" element={<ModeratorTopGames />} />
          <Route path="/moderator/hardlock" element={<ModeratorHardlock />} />
          <Route path="/admin/community-submissions" element={<AdminCommunitySubmissions />} />
        </>
      )}

      <Route path="/subscribe-free" element={<AdFree />} />
      <Route path="/ad-free" element={<AdFree />} />
      <Route path="/xxx" element={<Xxx />} />
      <Route path="/apply-translator" element={<ApplyTranslator />} />
      <Route path="/statistics" element={<Statistics />} />
      <Route path="/forum" element={<Forum />} />
      <Route path="/forum/category/:categoryName" element={<ForumTopicList />} />
      <Route path="/forum/topic/:topicId" element={<ForumTopicShow />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/blog/:id" element={<BlogShow />} />
      <Route path="/videos" element={<Videos />} />
      <Route path="/chat" element={<Chat />} />
      <Route path="/autotranslator" element={<Autotranslator />} />
      <Route path="/requests" element={<Requests />} />
      <Route path="/keres" element={<GameRequests />} />
      <Route path="/keres/:id" element={<GameRequestShow />} />
      <Route path="/translators" element={<Translators />} />
      <Route path="/support" element={<Support />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/emailok" element={<EmailConfirmationok />} />
      <Route path="/emailnok" element={<EmailConfirmationnok />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/settings" element={<UserSettings />} />
      <Route path="/user/themes" element={<UserThemeSettings />} />
      <Route path="/download/:id" element={<DownloadShow />} />
      <Route path="/subscriptions" element={<SubscriptionsPage />} />
      <Route path="/translator/:translatorId" element={<TranslatorProfile />} />
      <Route path="/games/:slug" element={<GameShow />} />
      <Route path="/egames/:slug" element={<EgameShow />} />
      <Route path="/game/:id/report-data" element={<ReportGameData />} />
      <Route path="/community-upload" element={<CommunityUpload />} />
      <Route path="/game/:id/request-update/step1" element={<RequestUpdateStep1 />} />
      <Route path="/game/:id/request-update/step2" element={<RequestUpdateStep2 />} />
      <Route path="/game/:id/request-update/step3" element={<RequestUpdateStep3 />} />
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
      <Route path="/bug-report" element={<BugReport />} />
      <Route path="/privacy" element={<Privacy />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/**
 * Átalakít egy hexadecimális színkódot HSL stringgé, amit a CSS változókhoz használhatunk.
 * @param hex A hexadecimális színkód (pl. "#RRGGBB").
 * @returns HSL értékek stringként (pl. "H S% L%").
 */
function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return ''; // Vagy egy alapértelmezett érték
  }

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = (max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4) / 6;
  }

  // Visszaadjuk a Tailwind által várt formátumban: "H S% L%"
  return `${(h * 360).toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%`;
}

const ThemeLoader = ({ children }: PropsWithChildren) => {
  const { data: themeSettings } = useQuery({
    queryKey: ['themeSettings'],
    queryFn: async () => {
      const response = await fetch('/api/theme_settings');
      if (!response.ok) {
        throw new Error('A téma betöltése sikertelen');
      }
      return response.json();
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 órán keresztül friss (admin tag-ből szerkeszthető)
    refetchOnWindowFocus: false,
    refetchOnmount: false,
  });

  useEffect(() => {
    if (themeSettings) {
      const root = document.documentElement;
      if (themeSettings.primary_color) {
        const hslColor = hexToHsl(themeSettings.primary_color);
        if (hslColor) root.style.setProperty('--primary', hslColor);
      }

      if (themeSettings.primary_foreground_color) {
        const hslForegroundColor = hexToHsl(themeSettings.primary_foreground_color);
        if (hslForegroundColor) root.style.setProperty('--primary-foreground', hslForegroundColor);
      }

      // A háttérkép beállítása a body-n a megfelelő vizuális hatásért
      const body = document.body;
      if (themeSettings.background_image_url) {
        body.style.backgroundImage = `url(${themeSettings.background_image_url})`;
        body.style.backgroundAttachment = 'fixed';
        body.style.backgroundPosition = 'center center';
        body.style.backgroundRepeat = 'no-repeat';
        body.style.backgroundSize = 'cover';
      } else {
        body.style.backgroundImage = 'none';
      }
    }
  }, [themeSettings]);

  return <>{children}</>;
}

const AppContent = () => {
  const { isAdModalOpen, setIsAdModalOpen } = useAdModal();

  return (
    <>
      <Toaster />
      <Sonner />
      {/* <Snowfall /> */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <BannedAlert />
        <AuthProvider>
          <CookieConsentProvider>
            <BanStatusChecker />
            <UsernameWarningBanner />
            <SupportBanner />
            <AppProvider>
              <ThemeLoader>
                <PrivacyGuard>
                  <PasswordCheckWrapper>
                    <MaintenanceGuard>
                      <AppRoutes />
                    </MaintenanceGuard>
                  </PasswordCheckWrapper>
                </PrivacyGuard>
              </ThemeLoader>
            </AppProvider>
            <CookieConsent />
          </CookieConsentProvider>
        </AuthProvider>
      </BrowserRouter>
      <AdRevenueModal isOpen={isAdModalOpen} onOpenChange={setIsAdModalOpen} />
    </>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AdModalProvider>
          <AppContent />
        </AdModalProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
